/**
 * Cart "Pyydä lasku" — opens the dialog, intercepts the contact-form submit,
 * sends it to Shopify's /contact endpoint via fetch, and on success redirects
 * the browser to a configurable thank-you page (default /pages/invoice-order).
 *
 * Why intercept: a normal {% form 'contact' %} submit redirects back to the
 * referrer URL with ?contact_posted=true, which is unreliable from the cart
 * page (Shopify sometimes lands on /). Driving the redirect from JS gives a
 * predictable success destination.
 *
 * Why delegated listeners: the cart summary that renders this markup is
 * re-rendered by the Section Rendering API on every cart change, and it is
 * also injected into the cart drawer from any page. Both paths replace the
 * button node — and a <script> that arrives inside injected HTML never
 * executes — so listeners bound directly to the button silently die and the
 * button stops responding. Binding once on `document` survives every
 * re-render, which is why this file is loaded from the layout rather than
 * from the snippet.
 */
(function () {
  // The layout loads this once, but guard anyway: the snippet used to ship its
  // own <script> tag, and a stale cached copy would otherwise double-bind.
  if (window.__cartInvoiceRequestReady) return;
  window.__cartInvoiceRequestReady = true;

  const DEFAULT_REDIRECT = '/pages/invoice-order';
  const SENDING_LABEL = 'Lähetetään…';

  /** @param {Element} el */
  const rootOf = (el) => el.closest('[data-cart-invoice-request]');

  /** @param {Element} root */
  const dialogOf = (root) => root.querySelector('[data-invoice-dialog]');

  const openDialog = (dialog) => {
    if (!dialog || dialog.open) return;
    if (typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
        return;
      } catch (err) {
        // showModal throws if the dialog is already in the top layer, or if it
        // is detached mid-morph. Fall through to the attribute fallback.
      }
    }
    dialog.setAttribute('open', '');
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) {
      try {
        dialog.close();
        return;
      } catch (err) {
        /* fall through */
      }
    }
    dialog.removeAttribute('open');
  };

  const feedbackOf = (root) => root.querySelector('[data-invoice-feedback]');

  const showError = (root, msg) => {
    const feedback = feedbackOf(root);
    if (!feedback) return;
    feedback.hidden = false;
    feedback.dataset.kind = 'error';
    feedback.textContent = msg;
  };

  const clearFeedback = (root) => {
    const feedback = feedbackOf(root);
    if (!feedback) return;
    feedback.hidden = true;
    feedback.textContent = '';
    delete feedback.dataset.kind;
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-invoice-open]');
    if (opener) {
      const root = rootOf(opener);
      if (!root) return;
      event.preventDefault();
      clearFeedback(root);
      openDialog(dialogOf(root));
      return;
    }

    const closer = target.closest('[data-invoice-close]');
    if (closer) {
      const root = rootOf(closer);
      if (!root) return;
      event.preventDefault();
      closeDialog(dialogOf(root));
      return;
    }

    // Backdrop click: the click lands on the <dialog> itself, not its contents
    if (target.matches('[data-invoice-dialog]')) {
      closeDialog(target);
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const root = rootOf(form);
    if (!root) return;

    // Native HTML5 validation has already passed by the time submit fires
    event.preventDefault();
    clearFeedback(root);

    const submitBtn = form.querySelector('[type="submit"]');
    const originalLabel = submitBtn ? submitBtn.textContent : '';

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = SENDING_LABEL;
    }

    try {
      const action = form.getAttribute('action') || '/contact';
      const res = await fetch(action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
        redirect: 'follow',
      });

      // Shopify returns 200 (with the redirect followed) on success; on a
      // validation error it re-renders the form page, also 200. Any non-network
      // response is treated as submitted and the destination page acts as the
      // confirmation. Network errors throw and are caught below.
      if (!res.ok && res.status >= 500) {
        throw new Error('Palvelinvirhe (' + res.status + ')');
      }

      window.location.assign(root.dataset.successRedirect || DEFAULT_REDIRECT);
    } catch (err) {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalLabel;
      }
      showError(root, 'Pyynnön lähettäminen epäonnistui: ' + err.message);
    }
  });
})();
